import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import BulkUploadContext from '../services/bulkUploadService';
import { getBulkUploadTimestamp } from '../services/bulkUploadTimestamp';
import { mergeUploads, useBulkUploads } from '../stores/bulkUploadStore';

const BASE_POLL_INTERVAL_MS = 12000;
const MAX_POLL_INTERVAL_MS = 60000;
const POLL_LIST_LIMIT = 10;
const MAX_DETAIL_IDS_PER_POLL = 3;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'error', 'cancelled']);

const normalizeStatus = (status) => String(status || '').toLowerCase();
const isMissingValue = (value) => value === null || value === undefined || value === '';
const hasResultLink = (upload) => Boolean(upload?.resultDownloadUrl);
const isTerminalStatus = (status) => TERMINAL_STATUSES.has(normalizeStatus(status));
const selectMostRecentUploads = (uploads, limit) => {
  if (!Array.isArray(uploads) || uploads.length <= limit) {
    return uploads;
  }
  return uploads
    .map((upload, index) => ({ upload, index }))
    .sort((left, right) => {
      const diff = getBulkUploadTimestamp(right.upload) - getBulkUploadTimestamp(left.upload);
      if (diff !== 0) {
        return diff;
      }
      return left.index - right.index;
    })
    .slice(0, limit)
    .map(({ upload }) => upload);
};
const isDetailIncomplete = (upload) => ([
  upload?.totalRowCount,
  upload?.createdCount,
  upload?.creationErrorCount,
  upload?.processingErrorCount,
  upload?.approvedCount,
  upload?.rejectedCount,
  upload?.otherCount,
  upload?.cancelledCount,
].some(isMissingValue));
const shouldRefreshDetails = (upload) => {
  if (!upload) {
    return false;
  }
  const status = normalizeStatus(upload.status);
  const terminal = isTerminalStatus(status);
  const completedWithoutResults = status === 'completed' && !hasResultLink(upload);
  const needsStatusRefresh = !terminal || completedWithoutResults;
  return isDetailIncomplete(upload) || needsStatusRefresh;
};

const useBulkUploadSummaryPolling = ({ detailUploadId } = {}) => {
  const { listUploadsWithDetails } = useContext(BulkUploadContext);
  const uploadsSnapshot = useBulkUploads();
  const uploadsRef = useRef(uploadsSnapshot);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pollIntervalRef = useRef(BASE_POLL_INTERVAL_MS);
  const timeoutRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const detailsRequestedRef = useRef(new Set());
  const paginationTokenRef = useRef(null);
  const hasPagedRef = useRef(false);

  const updatePollToken = useCallback((nextToken) => {
    const normalizedToken = nextToken || null;
    if (!hasPagedRef.current) {
      paginationTokenRef.current = normalizedToken;
      setHasMore(Boolean(normalizedToken));
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !paginationTokenRef.current) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const response = await listUploadsWithDetails({
        listInput: {
          limit: POLL_LIST_LIMIT,
          nextToken: paginationTokenRef.current,
        },
        maxPages: 1,
      });
      const uploads = Array.isArray(response?.uploads) ? response.uploads : [];
      const limitedUploads = selectMostRecentUploads(uploads, POLL_LIST_LIMIT);
      mergeUploads(limitedUploads);
      hasPagedRef.current = true;
      paginationTokenRef.current = response?.nextToken || null;
      setHasMore(Boolean(paginationTokenRef.current));
    } catch (err) {
      setError(err);
      console.error('Error loading more uploads:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, listUploadsWithDetails]);

  useEffect(() => {
    uploadsRef.current = uploadsSnapshot;
  }, [uploadsSnapshot]);

  useEffect(() => {
    let isActive = true;

    const scheduleNext = (delay) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(runPoll, delay);
    };

    const runPoll = async () => {
      if (!isActive) {
        return;
      }

      let requestedDetailIds = [];

      if (document?.visibilityState === 'hidden') {
        scheduleNext(MAX_POLL_INTERVAL_MS);
        return;
      }

      try {
        const missingDetailIds = (uploadsRef.current || [])
          .filter((upload) => (
            upload
            && upload.id
            && upload.id !== detailUploadId
            && shouldRefreshDetails(upload)
            && !detailsRequestedRef.current.has(upload.id)
          ))
          .slice(0, MAX_DETAIL_IDS_PER_POLL)
          .map((upload) => upload.id);

        missingDetailIds.forEach((uploadId) => {
          detailsRequestedRef.current.add(uploadId);
        });
        requestedDetailIds = missingDetailIds;

        const response = await listUploadsWithDetails({
          listInput: {
            limit: POLL_LIST_LIMIT,
          },
          maxPages: 1,
          detailUploadId,
          ...(missingDetailIds.length > 0 ? { detailUploadIds: missingDetailIds } : {}),
        });
        const uploads = Array.isArray(response?.uploads) ? response.uploads : [];
        const limitedUploads = selectMostRecentUploads(uploads, POLL_LIST_LIMIT);
        if (!isActive) return;
        updatePollToken(response?.nextToken);
        const details = Array.isArray(response?.details) ? response.details : [];
        mergeUploads(limitedUploads);
        if (Array.isArray(details) && details.length > 0) {
          mergeUploads(details);
        }
        if (requestedDetailIds.length > 0) {
          requestedDetailIds.forEach((uploadId) => {
            detailsRequestedRef.current.delete(uploadId);
          });
        }
        pollIntervalRef.current = BASE_POLL_INTERVAL_MS;
        setError(null);
      } catch (err) {
        if (!isActive) return;
        if (requestedDetailIds.length > 0) {
          requestedDetailIds.forEach((uploadId) => {
            detailsRequestedRef.current.delete(uploadId);
          });
        }
        setError(err);
        pollIntervalRef.current = Math.min(pollIntervalRef.current * 2, MAX_POLL_INTERVAL_MS);
        console.error('Error loading uploads:', err);
      } finally {
        if (!hasLoadedRef.current && isActive) {
          hasLoadedRef.current = true;
          setIsLoading(false);
        }
        if (isActive) {
          scheduleNext(pollIntervalRef.current);
        }
      }
    };

    const handleVisibility = () => {
      if (document?.visibilityState === 'visible') {
        pollIntervalRef.current = BASE_POLL_INTERVAL_MS;
        runPoll();
      }
    };

    runPoll();
    document?.addEventListener('visibilitychange', handleVisibility);

    return () => {
      isActive = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document?.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [detailUploadId, listUploadsWithDetails, updatePollToken]);

  return {
    isLoading,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
  };
};

export default useBulkUploadSummaryPolling;
